import { useState } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { company } from "../mock";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the form", description: "Name, email and message are required." });
      return;
    }
    setSubmitting(true);
    // Mock submission (frontend-only)
    setTimeout(() => {
      setSubmitting(false);
      toast({
        title: "Message sent",
        description: "Thank you for reaching out. Market Street Ventures will be in touch shortly.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    }, 800);
  };

  return (
    <div>
      <section className="relative bg-[#1a2540] py-24">
        <div className="mx-auto max-w-[1000px] px-6 text-center">
          <p className="font-heading uppercase tracking-[0.4em] text-[#4aa3e0] text-sm mb-4">
            Get In Touch
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-light uppercase tracking-[0.06em] text-white">
            Contact Us
          </h1>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1100px] px-6 grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info */}
          <div>
            <h2 className="font-heading text-3xl uppercase tracking-[0.06em] text-[#1a2540] mb-6">
              Let's Talk
            </h2>
            <div className="w-12 h-[3px] bg-[#1b75bc] mb-8" />
            <p className="text-[#6a6a6a] leading-relaxed mb-10 max-w-md">
              Start a conversation with Market Street Ventures. Whether it's cleaning solutions,
              absorbents, commodities or consulting — we would love to work with you.
            </p>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#e8f2fa] flex items-center justify-center shrink-0">
                  <MapPin size={20} className="text-[#1b75bc]" />
                </div>
                <div>
                  <p className="font-heading uppercase tracking-[0.1em] text-sm text-[#1a2540] mb-1">Address</p>
                  <p className="text-sm text-[#6a6a6a]">
                    {company.address.line1}
                    <br />
                    {company.address.line2}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#e8f2fa] flex items-center justify-center shrink-0">
                  <Phone size={20} className="text-[#1b75bc]" />
                </div>
                <div>
                  <p className="font-heading uppercase tracking-[0.1em] text-sm text-[#1a2540] mb-1">Phone</p>
                  <a href={`tel:${company.phoneRaw}`} className="text-sm text-[#6a6a6a] hover:text-[#1b75bc]">
                    {company.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#e8f2fa] flex items-center justify-center shrink-0">
                  <Mail size={20} className="text-[#1b75bc]" />
                </div>
                <div>
                  <p className="font-heading uppercase tracking-[0.1em] text-sm text-[#1a2540] mb-1">Email</p>
                  <a href={`mailto:${company.email}`} className="text-sm text-[#6a6a6a] hover:text-[#1b75bc]">
                    {company.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="bg-[#f6f8fa] p-8 md:p-10 border border-gray-100">
            <h3 className="font-heading text-2xl uppercase tracking-[0.06em] text-[#1a2540] mb-6">
              Send a Message
            </h3>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#4a4a4a] mb-2">Name *</label>
                <Input name="name" value={form.name} onChange={update} placeholder="Your name" className="bg-white" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-2">Email *</label>
                  <Input name="email" type="email" value={form.email} onChange={update} placeholder="you@email.com" className="bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-2">Phone</label>
                  <Input name="phone" value={form.phone} onChange={update} placeholder="(000) 000-0000" className="bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4a4a4a] mb-2">Message *</label>
                <Textarea name="message" value={form.message} onChange={update} rows={5} placeholder="How can we help you?" className="bg-white" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.18em] text-sm text-white bg-[#1b75bc] px-8 py-4 hover:bg-[#155e97] transition-colors duration-300 disabled:opacity-60"
              >
                <Send size={16} /> {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
